import { brl, milhar, esc } from './format';
import {
  activeBoosts, boostBadge, boostMessage, boostTitle, formatRemaining, isTurbo, remainingMs,
} from './boost';
import startToast from './toast';
import startBannerVideo from './bannerVideo';
import createUpsell from './upsell';
import { trackEvent } from '../single-page/utils/dataLayer';

// Página de venda alternativa de uma campanha. O HTML vem pronto do servidor
// (landing/show.html.erb); aqui entra só o que é vivo: cartões de pacote,
// quantidade livre, countdown, banner de boost/turbo, ganhadores, toast, modal
// de saída e o redirecionamento do botão "voltar".
// `config` é CampaignLandingConfig#as_json.

const $ = (selector, root = document) => root.querySelector(selector);
const $$ = (selector, root = document) => [...root.querySelectorAll(selector)];
const icon = (id) => `<svg class="ic" aria-hidden="true"><use href="#i-${id}"/></svg>`;

export const checkoutUrl = (config, quantity) => config.checkout.split('{qty}').join(encodeURIComponent(quantity));

// Selo de cada cartão de pacote, pela posição em relação ao recomendado.
export function tileLabel(quantity, packages, recommended) {
  const below = packages.filter((q) => q < recommended);
  const above = packages.filter((q) => q > recommended);
  if (quantity < recommended) return quantity === Math.min(...below) ? 'Inicial' : 'Intermediário';
  if (quantity === Math.max(...above)) return 'Mais chances';
  return `+${milhar(quantity - recommended)} títulos`;
}

// Linha de apoio: quanto custa a mais que o recomendado, ou quanto falta para
// levar o recomendado. O preço por título é o mesmo em todos os pacotes.
export function tileHint(quantity, recommended, price) {
  const diff = brl(Math.round(Math.abs(quantity - recommended) * price * 100) / 100);
  if (quantity > recommended) return `só <b>+${diff}</b> que o de ${milhar(recommended)}`;
  if (quantity < recommended) return `+<b>${diff}</b> e leve ${milhar(recommended)}`;
  return '';
}

// Próxima quantidade do seletor livre: anda em múltiplos do passo, sem sair
// dos limites do pedido (o mínimo pode não ser múltiplo do passo).
export function stepQuantity(quantity, direction, { step, min, max }) {
  const floor = Math.floor(quantity / step) * step;
  const next = direction > 0
    ? floor + step
    : (quantity % step === 0 ? quantity - step : floor);
  return Math.min(Math.max(next, min), max);
}

// Pedido de aumento: próximo múltiplo de 5 acima da quantidade (mesma regra do
// modal do site). Sem sugestão quando ela passaria do máximo do pedido.
export function suggestUpsell(quantity, max) {
  const suggested = (Math.floor(quantity / 5) + 1) * 5;
  return suggested <= max ? suggested : null;
}

// Mensagens de erro da criação do pedido (hash atributo → mensagens), em HTML.
export function orderErrors(data) {
  if (!data || typeof data !== 'object') return '';
  return Object.values(data).reduce((all, value) => all.concat(value), []).map(esc).join('<br>');
}

// Lê e remove da URL a compra pendente: recarregar ou voltar para a página não
// pode criar outro pedido. `comprar` é o parâmetro desta página; `tickets` é o
// da página de campanha antiga do site, que agora pode abrir aqui — quem
// estava no meio do login na hora da troca volta com ele.
export function takePendingPurchase() {
  const url = new URL(location.href);
  const keys = ['comprar', 'tickets'].filter((key) => url.searchParams.has(key));
  if (!keys.length) return null;

  const quantity = Number(url.searchParams.get(keys[0]));
  keys.forEach((key) => url.searchParams.delete(key));
  history.replaceState(history.state, '', url.pathname + url.search + url.hash);
  return quantity > 0 ? quantity : null;
}

export default function startLanding(config) {
  if (!config) return;

  const pendingPurchase = takePendingPurchase();

  const { price, recommended } = config;
  const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)').matches;
  const total = (quantity) => brl(Math.round(quantity * price * 100) / 100);
  const checkout = (quantity) => checkoutUrl(config, quantity);

  let subcampaigns = config.subcampaigns || [];

  /* ---- banner em vídeo (a imagem fica como poster e fallback) ---- */
  startBannerVideo($('.banner'));

  /* ---- cartões de pacote ---- */
  const tiles = $('#tiles');
  function renderTiles() {
    const boost = activeBoosts(subcampaigns)[0];
    const arrow = `<span class="go">${icon('arrow-right')}</span>`;
    const largest = config.packages.filter((q) => q > recommended).length > 0 ? Math.max(...config.packages) : null;

    tiles.innerHTML = config.packages.map((quantity, i) => {
      const hint = tileHint(quantity, recommended, price);
      const background = (config.tile_backgrounds || [])[i];
      return `
      <a class="tile${quantity === largest ? ' max' : ''}" style="--i:${i}" href="${esc(checkout(quantity))}" aria-label="Comprar ${milhar(quantity)} títulos por ${total(quantity)}">
        ${background ? `<span class="t-bg" style="background-image:url('${esc(background)}')" aria-hidden="true"></span>` : ''}
        <span class="t-top"><span class="st" aria-hidden="true">★</span>${tileLabel(quantity, config.packages, recommended)}<span class="st" aria-hidden="true">★</span></span>
        <span class="t-q num">${milhar(quantity)}</span><span class="t-u">títulos</span>
        ${boost ? `<span class="t-c t-boost${isTurbo(boost.active_boost) ? ' turbo' : ''}">${esc(boostBadge(boost.active_boost))}</span>` : ''}
        <span class="t-p num">${total(quantity)}</span>
        ${hint ? `<span class="t-x">${hint}</span>` : ''}
        <span class="t-gap"></span>
        <span class="t-b">Comprar ${arrow}</span>
      </a>`;
    }).join('') + `
      <button type="button" class="tile tile-custom" id="customToggle" style="--i:${config.packages.length}" aria-expanded="${!$('#customBox').hidden}" aria-controls="customBox">
        <span class="t-top">Do seu jeito</span>
        <span class="t-q">Outra</span><span class="t-u">quantidade</span>
        <span class="t-c">a partir de ${milhar(config.min)} ${config.min === 1 ? 'título' : 'títulos'}<br>${brl(price)} cada</span>
        <span class="t-b">Escolher ${icon('caret-down')}</span>
      </button>`;
    if (!config.packages.length) $('.more-head').hidden = true;
  }
  renderTiles();

  const customBox = $('#customBox');
  tiles.addEventListener('click', (event) => {
    const toggle = event.target.closest('#customToggle');
    if (!toggle) return;
    const opening = customBox.hidden;
    customBox.hidden = !opening;
    toggle.setAttribute('aria-expanded', String(opening));
    if (opening) customBox.scrollIntoView({ block: 'nearest', behavior: reducedMotion ? 'auto' : 'smooth' });
  });

  /* ---- quantidade livre ---- */
  let quantity = recommended;
  function setQuantity(next) {
    quantity = Math.min(Math.max(next, config.min), config.max);
    $('#qty').value = milhar(quantity);
    $('#cTotal').textContent = total(quantity);
    $('#cBuyLabel').textContent = `Comprar ${milhar(quantity)} títulos`;
    $('#cBuy').href = checkout(quantity);
    $('#minus').disabled = quantity <= config.min;
    $('#plus').disabled = quantity >= config.max;
  }
  $('#minus').addEventListener('click', () => setQuantity(stepQuantity(quantity, -1, config)));
  $('#plus').addEventListener('click', () => setQuantity(stepQuantity(quantity, 1, config)));
  setQuantity(quantity);

  /* ---- contagem regressiva até o sorteio ---- */
  const draw = $('#draw');
  const drawAt = config.draw_at ? new Date(config.draw_at).getTime() : NaN;
  if (draw && Number.isFinite(drawAt)) {
    const pad = (n) => String(n).padStart(2, '0');
    const parts = { d: $('[data-d]'), h: $('[data-h]'), m: $('[data-m]'), s: $('[data-s]') };
    const setPart = (key, value) => {
      const el = parts[key];
      if (el.textContent === value) return;
      el.textContent = value;
      el.classList.remove('tick'); void el.offsetWidth; el.classList.add('tick');
    };
    let timer = null;
    const tick = () => {
      const diff = drawAt - Date.now();
      if (!(diff > 0)) { // já passou: a página não promete um sorteio encerrado
        draw.hidden = true;
        clearInterval(timer);
        return;
      }
      const seconds = Math.floor(diff / 1000);
      setPart('d', pad(Math.floor(seconds / 86400)));
      setPart('h', pad(Math.floor((seconds % 86400) / 3600)));
      setPart('m', pad(Math.floor((seconds % 3600) / 60)));
      setPart('s', pad(seconds % 60));
      $('#clock').style.visibility = 'visible';
    };
    timer = setInterval(tick, 1000);
    tick();
  }

  /* ---- boost / turbo ---- */
  // Um banner por subcampanha com boost na janela (o backend só serializa
  // active_boost dentro dela; o turbo por link, só para quem tem o hash na
  // session). Ao zerar, recarrega o payload: pode haver outro período na
  // sequência, e os cartões voltam ao normal.
  const boostBox = $('#boost');
  const bar = $('#bar');
  let boostTimer = null;
  function renderBoost() {
    clearInterval(boostTimer);
    const boosted = activeBoosts(subcampaigns);
    $$('.bar-boost', bar).forEach((el) => el.remove());
    document.body.classList.toggle('boost-on', boosted.length > 0);
    if (!boosted.length) {
      boostBox.hidden = true;
      boostBox.innerHTML = '';
      return;
    }

    boostBox.innerHTML = boosted.map((sub) => {
      const boost = sub.active_boost;
      return `
      <div class="boost-card${isTurbo(boost) ? ' turbo' : ''}" data-ends="${esc(boost.ends_at)}">
        <div class="boost-head">
          <span>${esc(boostTitle(boost))}</span>
          <span class="boost-clock num">Termina em <b data-remaining></b></span>
        </div>
        <p>${esc(boostMessage(sub, boost))}</p>
      </div>`;
    }).join('');
    boostBox.hidden = false;

    const first = boosted[0].active_boost;
    $('.in', bar).insertAdjacentHTML('beforebegin', `<div class="bar-boost${isTurbo(first) ? ' turbo' : ''}">${esc(boostBadge(first))} · termina em <b class="num" data-remaining></b></div>`);

    const update = () => {
      const left = remainingMs(first.ends_at);
      $$('.boost-card', boostBox).forEach((card) => {
        $('[data-remaining]', card).textContent = formatRemaining(remainingMs(card.dataset.ends));
      });
      $$('.bar-boost [data-remaining]', bar).forEach((el) => { el.textContent = formatRemaining(left); });
      if (boosted.some((sub) => remainingMs(sub.active_boost.ends_at) <= 0)) {
        clearInterval(boostTimer);
        refreshSubcampaigns();
      }
    };
    boostTimer = setInterval(update, 1000);
    update();
  }
  function refreshSubcampaigns() {
    const done = () => { renderBoost(); renderTiles(); };
    if (typeof fetch !== 'function') { subcampaigns = []; done(); return; }
    fetch(config.urls.campaign, { credentials: 'same-origin', headers: { Accept: 'application/json' } })
      .then((response) => (response.ok ? response.json() : {}))
      .then((campaign) => { subcampaigns = campaign.active_subcampaigns || []; })
      .catch(() => { subcampaigns = []; })
      .then(done);
  }
  renderBoost();

  /* ---- ganhadores ---- */
  const winnersSection = $('#ganhadores');
  if (typeof fetch === 'function') {
    fetch(config.urls.winners, { credentials: 'same-origin', headers: { Accept: 'application/json' } })
      .then((response) => (response.ok ? response.json() : []))
      .then((draws) => {
        const winners = (draws || []).filter((d) => Number(d.prize_value) > 0 && (d.drawn_customer || d.drawn_to_entity));
        if (!winners.length) return;

        $('#winners').innerHTML = winners.map((d) => {
          const top = Number(d.prize_value) >= 50000;
          const date = d.draw_time ? new Date(d.draw_time).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' }) : '';
          const name = d.drawn_to_entity ? 'Prêmio da entidade' : d.drawn_customer.name;
          const line = [d.title, date, d.drawn_customer && d.drawn_customer.cpf ? `CPF ${d.drawn_customer.cpf}` : null].filter(Boolean);
          const lottery = [d.federal_draw_label, d.drawn_ticket_number != null ? `sorteado ${d.drawn_ticket_number}` : null].filter(Boolean);
          return `
          <article class="win${top ? ' top' : ''}">
            <span class="chip">${icon(top ? 'trophy' : 'medal')}</span>
            <div>
              <div class="w-h"><h3>${esc(name)}</h3><strong class="num">${brl(d.prize_value).replace(',00', '')}</strong></div>
              <p>${line.map(esc).join(' · ')}</p>
              ${lottery.length ? `<p class="lf">${lottery.map(esc).join(' · ')}</p>` : ''}
            </div>
          </article>`;
        }).join('');

        const paid = winners.reduce((sum, d) => sum + Number(d.prize_value), 0);
        $('#paid').textContent = paid >= 1000 ? `R$ ${milhar(Math.round(paid / 1000))} mil` : brl(paid);
        $('#paidCount').textContent = winners.length;
        winnersSection.hidden = false;
      })
      .catch(() => {});
  }

  /* ---- toast de ganhadores instantâneos ---- */
  startToast($('#iwToast'), config.urls.instant_winners);

  /* ---- botão voltar: leva para o checkout do pacote logo abaixo do recomendado (uma vez por visita) ---- */
  // O Chrome PULA, no botão voltar, as entradas de histórico criadas antes de a
  // pessoa interagir com a página — por isso a entrada extra só é criada depois
  // do primeiro toque/clique/tecla (no Safari, que não tem essa regra nem a API
  // userActivation, cria na hora).
  const backQuantity = config.back_quantity;
  const initialUrl = location.href;
  const EVENTS = ['pointerdown', 'pointerup', 'touchend', 'keydown', 'click'];
  let armed = false;
  let leftOnce = false;
  function armBack() {
    if (armed || !backQuantity || !history.pushState) return;
    const activation = navigator.userActivation;
    if (activation && !activation.hasBeenActive) return;
    armed = true;
    EVENTS.forEach((type) => removeEventListener(type, armBack, true));
    try { history.pushState({ edsSaida: 1 }, '', location.href); } catch (error) { armed = false; }
  }
  addEventListener('popstate', (event) => {
    if (!armed || leftOnce) return;
    if (event.state && event.state.edsSaida) return; // voltou para a própria entrada (ex.: saiu de uma âncora)
    if (location.href !== initialUrl) return; // navegação para uma âncora dispara popstate no Chrome
    leftOnce = true;
    location.replace(checkout(backQuantity));
  });
  EVENTS.forEach((type) => addEventListener(type, armBack, true));
  armBack();
  // o logo volta ao topo sem criar entrada no histórico (senão o "voltar" seguinte redirecionaria à toa)
  $('#logo').addEventListener('click', (event) => {
    event.preventDefault();
    scrollTo({ top: 0, behavior: reducedMotion ? 'auto' : 'smooth' });
  });

  /* ---- modais (saída, pedido de aumento, criação do pedido) ---- */
  const backdrop = () => [$('header'), $('main'), $('footer'), bar, $('#fab')].filter(Boolean);
  let focusBefore = null;
  function showOverlay(overlay, focusTarget) {
    if ($$('.exit').every((el) => el.hidden)) focusBefore = document.activeElement;
    overlay.hidden = false;
    document.body.classList.add('travado');
    backdrop().forEach((el) => { el.inert = true; });
    if (focusTarget) focusTarget.focus({ preventScroll: true });
  }
  function hideOverlay(overlay) {
    overlay.hidden = true;
    if ($$('.exit').some((el) => !el.hidden)) return; // outro modal assumiu a tela
    document.body.classList.remove('travado');
    backdrop().forEach((el) => { el.inert = false; });
    bar.inert = !bar.classList.contains('on');
    if (focusBefore && focusBefore.focus) focusBefore.focus({ preventScroll: true });
  }

  /* ---- compra: cria o pedido daqui e segue direto para o pagamento ---- */
  const buying = $('#buying');
  const buyingTitle = $('#buyingT');
  function buyingFailed(message) {
    buyingTitle.textContent = buyingTitle.dataset.failed;
    $('#buyingError').innerHTML = message;
    $('#buyingError').hidden = !message;
    $('#buyingClose').hidden = false;
    $('#buyingClose').focus({ preventScroll: true });
  }
  $('#buyingClose').addEventListener('click', () => hideOverlay(buying));

  function createOrder(ticketsAmount) {
    // Sem login o pedido não existe: o servidor guarda a volta, manda para o
    // login e a página cria o pedido sozinha ao retornar (?comprar=N).
    if (!config.logged_in || typeof fetch !== 'function') {
      location.href = checkout(ticketsAmount);
      return;
    }

    buyingTitle.textContent = buyingTitle.dataset.creating;
    $('#buyingError').hidden = true;
    $('#buyingClose').hidden = true;
    showOverlay(buying);

    fetch(config.urls.orders, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
      body: JSON.stringify({ purchase_order: { campaign_id: config.campaign_id, tickets_amount: ticketsAmount } }),
    })
      .then((response) => response.json().catch(() => ({})).then((data) => ({ response, data })))
      .then(({ response, data }) => {
        if (response.status === 401) { // sessão caiu com a página aberta
          location.href = checkout(ticketsAmount);
          return;
        }
        if (!response.ok || !data.id) {
          buyingFailed(orderErrors(data));
          return;
        }
        trackEvent(
          'InitiateCheckout',
          {
            value: parseFloat(data.total_value),
            currency: 'BRL',
            ...(data.affiliate_name && { affiliate_name: data.affiliate_name }),
          },
          { dedupe: false, persistOnRedirect: true },
        );
        location.href = config.urls.order.split('{id}').join(encodeURIComponent(data.id));
      })
      .catch(() => buyingFailed(''));
  }

  /* ---- pedido de aumento: próximo múltiplo de 5, como no site ---- */
  const upsell = createUpsell($('#upsell'), {
    show: showOverlay, hide: hideOverlay, total, onConfirm: createOrder,
  });

  // Todo botão de compra é um link para o checkout do servidor (funciona sem
  // JS). Aqui o clique vira: pedido de aumento → pedido criado. O link da
  // oferta do modal de saída (saida=1) segue pelo servidor, que é quem libera
  // o mínimo da oferta na session.
  document.addEventListener('click', (event) => {
    const link = event.target.closest && event.target.closest('a[href*="/comprar?"]');
    if (!link) return;
    const params = new URL(link.href, location.href).searchParams;
    const ticketsAmount = Number(params.get('qty'));
    if (!ticketsAmount || params.has('saida')) return;

    event.preventDefault();
    if (!exit.hidden) closeModal();
    const suggested = 'direct' in link.dataset ? null : suggestUpsell(ticketsAmount, config.max);
    if (suggested) upsell.ask(ticketsAmount, suggested); else createOrder(ticketsAmount);
  });

  /* ---- modal de saída: no computador, quando o mouse sai pelo topo da janela; uma vez por sessão ---- */
  const exit = $('#exit');
  const remember = (key, value) => {
    try { return value === undefined ? sessionStorage.getItem(key) : sessionStorage.setItem(key, value); } catch (error) { return null; }
  };
  let modalOpen = false;
  let modalShown = false;
  function closeModal() {
    modalOpen = false;
    hideOverlay(exit);
    const video = $('.exit-vid');
    if (video) video.pause();
  }
  function openModal() {
    if (modalShown || $$('.exit').some((el) => !el.hidden)) return;
    modalShown = true; remember('edsModal', '1');
    const video = $('.exit-vid');
    if (video && video.dataset.src && !reducedMotion) {
      if (!video.getAttribute('src')) {
        video.src = video.dataset.src;
        video.loop = true;
        // alguns navegadores pausam o vídeo sozinhos; enquanto o modal estiver aberto, retoma
        video.addEventListener('pause', () => setTimeout(() => { if (modalOpen && video.paused) video.play().catch(() => {}); }, 250));
      }
      video.play().catch(() => {});
    }
    modalOpen = true;
    showOverlay(exit, $('#exitBuy'));
  }
  if (!remember('edsModal') && matchMedia('(hover:hover) and (pointer:fine)').matches) {
    const startedAt = Date.now();
    document.addEventListener('mouseout', (event) => {
      // só quando o mouse sai pelo topo (barra do navegador), depois de 3 s na página
      if (event.relatedTarget || event.clientY > 0 || Date.now() - startedAt < 3000) return;
      openModal();
    });
  }
  $('#exitX').addEventListener('click', closeModal);
  $('#exitLeave').addEventListener('click', closeModal);
  exit.addEventListener('click', (event) => { if (event.target === exit) closeModal(); });
  addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    if (modalOpen) closeModal();
    else upsell.cancel();
  });

  // Volta do login (ou do checkout do servidor) com ?comprar=N: cria o pedido
  // sem perguntar de novo. Sem sessão o parâmetro é ignorado.
  if (pendingPurchase && config.logged_in) createOrder(pendingPurchase);

  /* ---- animações de entrada + barra fixa ---- */
  if ('IntersectionObserver' in window) {
    const reveal = new IntersectionObserver((entries) => entries.forEach((entry) => {
      if (!entry.isIntersecting) return;
      entry.target.classList.remove('hide');
      reveal.unobserve(entry.target);
    }), { threshold: 0.12 });
    $$('.reveal').forEach((el) => {
      // só esconde o que está abaixo da dobra; se algo acima falhar, nada fica invisível
      if (!reducedMotion && el.getBoundingClientRect().top > innerHeight) el.classList.add('hide');
      reveal.observe(el);
    });

    // barra fixa: aparece quando o cartão principal sai da tela
    new IntersectionObserver(([entry]) => {
      const on = !entry.isIntersecting;
      bar.classList.toggle('on', on);
      bar.inert = !on;
      document.body.classList.toggle('bar-on', on);
    }, { threshold: 0.1 }).observe($('#heroCard'));
  } else {
    bar.classList.add('on');
    bar.inert = false;
    document.body.classList.add('bar-on');
  }

  /* ---- tracking: mesmo evento e payload da página de campanha do site ---- */
  trackEvent(
    'ViewContent',
    {
      value: price,
      currency: 'BRL',
      content_name: config.title,
      content_ids: [String(config.campaign_id)],
      content_type: 'product',
      contents: [{ id: String(config.campaign_id), name: config.title, quantity: 1, price }],
      ...(config.affiliate_name && { affiliate_name: config.affiliate_name }),
    },
    { dedupe: false },
  );
}
